import {
  ANALYTICS_SCHEMA_VERSION,
  ANALYTICS_SOURCE,
  ChatAnalyticsEvent,
  getAnalyticsAction,
  getAnalyticsDomain,
} from './events';
import { hashUserId } from './identity';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    clarity?: (...args: unknown[]) => void;
  }
}

export interface AnalyticsConfig {
  ga4MeasurementId?: string;
  clarityProjectId?: string;
  debug: boolean;
}

export interface AnalyticsIdentityInput {
  userId?: string | null;
  accountId?: string | null;
}

export type AnalyticsEventParams = Record<string, unknown>;
type ClarityShim = ((...args: unknown[]) => void) & { q?: unknown[] };

/**
 * Deployment templates ship `TBD` as a placeholder for not-yet-provisioned IDs.
 * Treat it as "unset" so a templated deployment stays disabled instead of
 * emitting telemetry to an invalid destination.
 */
const PLACEHOLDER_CONFIG_VALUES = new Set(['tbd', 'todo', 'changeme', 'none']);

function normalizeConfigId(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  if (PLACEHOLDER_CONFIG_VALUES.has(trimmed.toLowerCase())) return undefined;
  return trimmed;
}

const analyticsConfig: AnalyticsConfig = {
  ga4MeasurementId: normalizeConfigId(process.env.REACT_APP_GA_MEASUREMENT_ID),
  clarityProjectId: normalizeConfigId(process.env.REACT_APP_CLARITY_PROJECT_ID),
  debug: process.env.REACT_APP_ANALYTICS_DEBUG === 'true',
};

let initialized = false;
let currentHashedUserId: string | undefined;
let currentAccountId: string | undefined;
let lastTrackedPage: string | undefined;
let appSessionId: string | undefined;
let pendingIdentifyCount = 0;
let deferredPageView = false;
let consentGranted = false;
let identityGeneration = 0;
let clarityStopped = false;
let vendorsInitialized = false;

const APP_SESSION_KEY = 'assistant_analytics_app_session_id';

/**
 * Query strings and fragments can carry secrets — the Keycloak redirect lands on
 * `/callback?code=...&state=...`. Never forward them to GA4 or Clarity (which
 * records session replays), so only the pathname is ever reported.
 */
function getSanitizedPagePath(): string {
  return window.location.pathname || '/';
}

function getSanitizedPageLocation(): string {
  return `${window.location.origin}${getSanitizedPagePath()}`;
}

function appendScript(id: string, src: string, async = true): void {
  if (document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.src = src;
  script.async = async;
  document.head.appendChild(script);
}

function initializeGa4(measurementId: string): void {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtagShim() {
    // Google's dispatcher recognizes native Arguments objects, not arrays.
    window.dataLayer?.push(arguments);
  };
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
  });
  window.gtag('consent', 'update', { analytics_storage: 'granted' });
  Object.assign(window, { [`ga-disable-${measurementId}`]: false });
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false,
    user_id: currentHashedUserId,
  });
  window.gtag('set', 'user_properties', {
    source: ANALYTICS_SOURCE,
    account_id: currentAccountId,
  });
  appendScript(
    'assistant-ga4',
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
  );
}

function initializeClarity(projectId: string): void {
  if (!window.clarity) {
    const clarityShim: ClarityShim = (...args: unknown[]) => {
      clarityShim.q = clarityShim.q || [];
      clarityShim.q.push(args);
    };
    window.clarity = clarityShim;
  }
  appendScript('assistant-clarity', `https://www.clarity.ms/tag/${encodeURIComponent(projectId)}`);
  if (clarityStopped) window.clarity('start');
  clarityStopped = false;
  window.clarity('consentv2', { ad_Storage: 'denied', analytics_Storage: 'granted' });
}

export function deleteAnalyticsCookies(): void {
  const host = window.location.hostname;
  const parts = host.split('.');
  const domains = [undefined, host, ...parts.slice(1).map((_, index) => parts.slice(index + 1).join('.'))];
  for (const cookie of document.cookie.split(';')) {
    const name = cookie.trim().split('=')[0];
    if (!/^(_ga($|_)|_clck$|_clsk$)/.test(name)) continue;
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; Path=/${domain ? `; Domain=${domain}` : ''}`;
    }
  }
}

export function isDevAnalyticsHost(): boolean {
  return ['assistant-dev.artstash.io', 'localhost', '127.0.0.1'].includes(window.location.hostname);
}

function logDebug(message: string, data?: unknown): void {
  if (analyticsConfig.debug) {
    // eslint-disable-next-line no-console
    console.debug(`[analytics] ${message}`, data);
  }
}

function getAppSessionId(): string {
  if (appSessionId) return appSessionId;
  const stored = sessionStorage.getItem(APP_SESSION_KEY);
  if (stored) {
    appSessionId = stored;
    return stored;
  }
  const generated =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  sessionStorage.setItem(APP_SESSION_KEY, generated);
  appSessionId = generated;
  return generated;
}

function withIdentityParams(params: AnalyticsEventParams): AnalyticsEventParams {
  const payload: AnalyticsEventParams = { ...params };
  const accountId = params.account_id ?? currentAccountId;
  const userIdHash = params.user_id_hash ?? currentHashedUserId;

  if (accountId) {
    payload.account_id = accountId;
  }
  if (userIdHash) {
    payload.user_id_hash = userIdHash;
  }
  return payload;
}

export const analytics = {
  getConfig(): AnalyticsConfig {
    return { ...analyticsConfig };
  },

  isEnabled(): boolean {
    return consentGranted && isDevAnalyticsHost() &&
      Boolean(analyticsConfig.ga4MeasurementId || analyticsConfig.clarityProjectId);
  },

  setConsent(granted: boolean): void {
    if (granted && isDevAnalyticsHost()) {
      consentGranted = true;
      this.initialize();
      window.dispatchEvent(new Event('assistant-analytics-consent'));
      return;
    }
    consentGranted = false;
    identityGeneration += 1;
    if (analyticsConfig.ga4MeasurementId) {
      Object.assign(window, { [`ga-disable-${analyticsConfig.ga4MeasurementId}`]: true });
      if (vendorsInitialized) {
        window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
        window.gtag?.('set', { user_id: null });
        window.gtag?.('set', 'user_properties', { account_id: null });
      }
    }
    if (vendorsInitialized && analyticsConfig.clarityProjectId) {
      window.clarity?.('consentv2', { ad_Storage: 'denied', analytics_Storage: 'denied' });
      window.clarity?.('consent', false);
      window.clarity?.('stop');
      clarityStopped = true;
    }
    // Drop unprocessed pre-withdrawal events if the vendor scripts are still loading.
    if (window.dataLayer) {
      const revocationCommands = vendorsInitialized && analyticsConfig.ga4MeasurementId
        ? window.dataLayer.slice(-3) : [];
      window.dataLayer.splice(0, window.dataLayer.length, ...revocationCommands);
    }
    const shim = window.clarity as ClarityShim | undefined;
    if (shim?.q) {
      shim.q = [
        ['consentv2', { ad_Storage: 'denied', analytics_Storage: 'denied' }],
        ['consent', false], ['stop'],
      ];
    }
    currentHashedUserId = undefined;
    currentAccountId = undefined;
    lastTrackedPage = undefined;
    deferredPageView = false;
    appSessionId = undefined;
    sessionStorage.removeItem(APP_SESSION_KEY);
    initialized = false;
    deleteAnalyticsCookies();
  },

  initialize(): void {
    if (initialized || !this.isEnabled()) return;
    initialized = true;
    vendorsInitialized = true;

    if (analyticsConfig.ga4MeasurementId) {
      initializeGa4(analyticsConfig.ga4MeasurementId);
    }
    if (analyticsConfig.clarityProjectId) {
      initializeClarity(analyticsConfig.clarityProjectId);
    }
    logDebug('initialized', analyticsConfig);
  },

  async identify(input: AnalyticsIdentityInput): Promise<void> {
    if (!this.isEnabled()) return;
    this.initialize();
    const generation = identityGeneration;
    // Incremented synchronously so a page view emitted later in the same commit
    // (route change + identity change land together) waits for the new identity.
    pendingIdentifyCount += 1;
    try {
      const nextAccountId = input.accountId || undefined;
      const nextHashedUserId = input.userId ? await hashUserId(input.userId) : undefined;
      if (!this.isEnabled() || generation !== identityGeneration) return;
      const accountChanged = currentAccountId !== nextAccountId;

      currentHashedUserId = nextHashedUserId;
      currentAccountId = nextAccountId;
      if (accountChanged) {
        lastTrackedPage = undefined;
      }

      if (analyticsConfig.ga4MeasurementId && window.gtag) {
        window.gtag('config', analyticsConfig.ga4MeasurementId, {
          user_id: currentHashedUserId,
          send_page_view: false,
        });
        window.gtag('set', 'user_properties', {
          source: ANALYTICS_SOURCE,
          account_id: currentAccountId,
        });
      }
      if (analyticsConfig.clarityProjectId && window.clarity) {
        if (currentHashedUserId) {
          window.clarity('identify', currentHashedUserId);
        }
        if (currentAccountId) {
          window.clarity('set', 'account_id', currentAccountId);
        }
        window.clarity('set', 'source', ANALYTICS_SOURCE);
      }
      logDebug('identified', { user: currentHashedUserId, account: currentAccountId });
    } finally {
      pendingIdentifyCount -= 1;
    }

    if (pendingIdentifyCount === 0 && (deferredPageView || currentAccountId)) {
      deferredPageView = false;
      this.trackPageView();
    }
  },

  clearIdentity(): void {
    this.setConsent(false);
  },

  trackPageView(): void {
    this.initialize();
    if (!this.isEnabled()) return;
    if (pendingIdentifyCount > 0) {
      // Identity is mid-flight; emitting now would attribute this page view to
      // the previous account. identify() flushes the deferred page view.
      deferredPageView = true;
      logDebug('deferred page_view until identity reconciles');
      return;
    }
    if (currentHashedUserId && !currentAccountId) {
      logDebug('skipped authenticated page_view without account_id');
      return;
    }
    const pagePath = getSanitizedPagePath();
    if (lastTrackedPage === pagePath) return;
    lastTrackedPage = pagePath;

    const payload = withIdentityParams({
      page_title: document.title,
      page_location: getSanitizedPageLocation(),
      page_path: pagePath,
      source: ANALYTICS_SOURCE,
      domain: 'page',
      schema_version: ANALYTICS_SCHEMA_VERSION,
      app_session_id: getAppSessionId(),
    });
    if (analyticsConfig.ga4MeasurementId && window.gtag) {
      window.gtag('event', 'page_view', payload);
    }
    if (analyticsConfig.clarityProjectId && window.clarity) {
      window.clarity('set', 'page_path', pagePath);
    }
    logDebug('page_view', payload);
  },

  track(eventName: ChatAnalyticsEvent, params: AnalyticsEventParams = {}): void {
    this.initialize();
    if (!this.isEnabled()) return;
    const analyticsAction = getAnalyticsAction(eventName);
    const payload = withIdentityParams({
      ...params,
      event_type: eventName,
      source: ANALYTICS_SOURCE,
      domain: params.domain ?? getAnalyticsDomain(eventName),
      schema_version: params.schema_version ?? ANALYTICS_SCHEMA_VERSION,
      // Always authoritative: callers must not override the stable app session.
      app_session_id: getAppSessionId(),
    });
    if (currentHashedUserId && !payload.account_id) {
      logDebug('skipped authenticated event without account_id', { eventName });
      return;
    }
    if (analyticsConfig.ga4MeasurementId && window.gtag) {
      window.gtag('event', analyticsAction, payload);
    }
    if (analyticsConfig.clarityProjectId && window.clarity) {
      // Clarity events carry no parameters, so the canonical name must be sent
      // whole — the bare action would collapse `assistant.tool.invoked` and
      // `assistant.qc.invoked` into a single `invoked` event.
      window.clarity('event', eventName);
    }
    logDebug(eventName, payload);
  },
};
